/**
 * NA-5815 / NA-5831 — keep the Approval-list search across Edit / Approve / Reject.
 *
 * Two sessionStorage keys:
 *  - snapshot: the list URL (search incl. the drawer `id`) and the drawer row
 *    index, written by the list when the user leaves it (Edit / Detail click).
 *  - should_restore: the "return intent", written only when the action
 *    succeeded — by the React edit page after save (`markShouldRestore`), and
 *    by Rails after approve / reject, in the list view before it redirects
 *    back here. It says whether to reopen the same request (edit) or the next
 *    one at the saved row (approve / reject).
 *
 * The list restores the saved search only when both exist, then deletes both.
 * Any other way back to the list deletes the snapshot and behaves as before.
 */

export const APPROVING_LIST_SNAPSHOT_KEY =
  'approving_invoice_reports:list_snapshot';
/**
 * Shared contract with ap_web. Rails writes exactly
 * `{"shouldRestoreSearchParams":true,"shouldOpenNextPayment":true}` after approve / reject.
 */
export const APPROVING_LIST_SHOULD_RESTORE_KEY =
  'approving_invoice_reports:should_restore';

/** Query param the drawer stores its open report in. */
export const DRAWER_ID_PARAM = 'id';

export type ListSnapshot = {
  /** Query string of the list, including the drawer `id` when it was open. */
  search: string;
  /** Row index of the open drawer, for "open the next request" after approve / reject. */
  selectedIndex?: number;
  /**
   * The request the user left the list for. The edit page only redirects back
   * to the list when this matches the request being edited, so a snapshot left
   * behind for another request (user never returned to the list) is ignored.
   */
  reportId?: string;
};

export type ReturnIntent = {
  /** Always true while the key exists; kept so the stored value is self-describing. */
  shouldRestoreSearchParams: true;
  /**
   * approve / reject → true: the processed request must not reopen, show the
   * one now at the saved row instead. edit → false: reopen the edited request.
   */
  shouldOpenNextPayment: boolean;
};

const storage = (): Storage | undefined => {
  try {
    return window.sessionStorage;
  } catch {
    return undefined;
  }
};

const withoutDrawerId = (search: string): string => {
  const params = new URLSearchParams(search);
  params.delete(DRAWER_ID_PARAM);
  const str = params.toString();
  return str ? `?${str}` : '';
};

/** true when the URL carries a real search (anything besides the drawer `id`). */
export const hasExplicitSearch = (search: string): boolean =>
  withoutDrawerId(search) !== '';

export const saveListSnapshot = (snapshot: ListSnapshot): void => {
  try {
    storage()?.setItem(APPROVING_LIST_SNAPSHOT_KEY, JSON.stringify(snapshot));
  } catch {
    // storage unavailable — behave as today
  }
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const isListSnapshot = (value: unknown): value is ListSnapshot =>
  isRecord(value) &&
  typeof value['search'] === 'string' &&
  (value['selectedIndex'] === undefined ||
    typeof value['selectedIndex'] === 'number') &&
  (value['reportId'] === undefined || typeof value['reportId'] === 'string');

/** The saved snapshot, or undefined when the key is missing or not a valid snapshot. */
export const readListSnapshot = (): ListSnapshot | undefined => {
  const raw = storage()?.getItem(APPROVING_LIST_SNAPSHOT_KEY);
  if (!raw) return undefined;
  try {
    const parsed: unknown = JSON.parse(raw);
    return isListSnapshot(parsed) ? parsed : undefined;
  } catch {
    return undefined;
  }
};

/** Written only when the edit / approve / reject succeeded. */
export const markShouldRestore = ({
  shouldOpenNextPayment,
}: Pick<ReturnIntent, 'shouldOpenNextPayment'>): void => {
  try {
    const intent: ReturnIntent = {
      shouldRestoreSearchParams: true,
      shouldOpenNextPayment,
    };
    storage()?.setItem(
      APPROVING_LIST_SHOULD_RESTORE_KEY,
      JSON.stringify(intent)
    );
  } catch {
    // storage unavailable — behave as today
  }
};

/** The return intent, or undefined when the key is missing or not a valid intent. */
export const readReturnIntent = (): ReturnIntent | undefined => {
  const raw = storage()?.getItem(APPROVING_LIST_SHOULD_RESTORE_KEY);
  if (!raw) return undefined;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!isRecord(parsed) || parsed['shouldRestoreSearchParams'] !== true)
      return undefined;
    return {
      shouldRestoreSearchParams: true,
      shouldOpenNextPayment: parsed['shouldOpenNextPayment'] === true,
    };
  } catch {
    return undefined;
  }
};

export const readShouldRestore = (): boolean =>
  readReturnIntent() !== undefined;

export const clearListRestoreKeys = (): void => {
  try {
    storage()?.removeItem(APPROVING_LIST_SNAPSHOT_KEY);
    storage()?.removeItem(APPROVING_LIST_SHOULD_RESTORE_KEY);
  } catch {
    // storage unavailable — behave as today
  }
};

/**
 * Query string the list should start from.
 *  - URL already has a search → the URL (bookmark / shared link wins)
 *  - no should_restore key    → the URL (the stale snapshot is deleted later
 *                               by useRestoreDrawerAfterReturn, which owns
 *                               all deletes; this function only reads)
 *  - both keys, edit          → the saved search with its drawer `id`
 *  - both keys, approve/reject→ the saved search without the `id`: the
 *                               processed request must not reopen, the next
 *                               one is chosen after the first fetch
 *                               (useRestoreDrawerAfterReturn)
 */
export const resolveInitialListSearch = (locationSearch: string): string => {
  if (hasExplicitSearch(locationSearch)) return locationSearch;
  const intent = readReturnIntent();
  if (!intent) return locationSearch;
  const saved = readListSnapshot()?.search;
  if (!saved) return locationSearch;
  return intent.shouldOpenNextPayment ? withoutDrawerId(saved) : saved;
};
